import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Card, Spin, DatePicker, Button, Space,
  Table, Tag, Alert, Select, Radio, Tabs,
} from 'antd';
import {
  FileDoneOutlined, WarningOutlined, ReloadOutlined,
  CalendarOutlined, DownloadOutlined, ShopOutlined,
  TeamOutlined, WalletOutlined, TrophyOutlined,
  BoxPlotOutlined, RiseOutlined, FallOutlined,
} from '@ant-design/icons';
import { dashboard, inventory } from '../api';
import { fmtCurrency } from '../utils/format';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;
const { Option } = Select;

const fmtQty = (v) => {
  const n = Number(v ?? 0);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
};

function exportToExcel(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows, headers }) => {
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    XLSX.utils.book_append_sheet(wb, ws, name.substring(0, 31));
  });
  XLSX.writeFile(wb, filename);
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 8,
      padding: '14px 16px',
      height: '100%',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <Row align="middle" gutter={12} wrap={false}>
        <Col flex="none">
          <div style={{
            background: color + '18',
            borderRadius: 10,
            padding: 10,
            color,
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </div>
        </Col>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
            {label}
          </div>
          <div style={{
            fontSize: 17, fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {value}
          </div>
          {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
        </Col>
      </Row>
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function DashCard({ title, children, extra, style }) {
  return (
    <Card
      title={<span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</span>}
      extra={extra}
      size="small"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', height: '100%', ...style }}
      headStyle={{ borderBottom: '1px solid var(--border)', background: 'var(--surface2, var(--surface))', padding: '0 14px', minHeight: 40 }}
      bodyStyle={{ padding: '12px 14px' }}
    >
      {children}
    </Card>
  );
}

// ─── Rank Table ───────────────────────────────────────────────────────────────
function RankTable({ data, columns, rowKey = 'name' }) {
  if (!data?.length) return (
    <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '28px 0', fontSize: 12 }}>
      No data for selected period
    </div>
  );
  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={rowKey}
      size="small"
      pagination={false}
      scroll={{ x: 'max-content' }}
      style={{ fontSize: 12 }}
    />
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [stats,    setStats]    = useState(null);
  const [topProds, setTopProds] = useState({});
  const [topCusts, setTopCusts] = useState({});
  const [lowStock, setLowStock] = useState([]);
  const [invData,  setInvData]  = useState({ saleProducts: [], rentalProducts: [] });
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [prodSort, setProdSort] = useState('desc');
  const [custSort, setCustSort] = useState('desc');
  const [limit,    setLimit]    = useState(10);

  const LIMIT_OPTS = [5, 10, 20, 0];

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [start, end] = dateRange || [];
      const s1 = start ? start.format('YYYY-MM-DD') : null;
      const s2 = end   ? end.format('YYYY-MM-DD')   : null;
      const [st, tp, tc, ls, inv] = await Promise.all([
        dashboard.stats(s1, s2),
        dashboard.topProducts(limit, s1, s2),
        dashboard.topCustomers(limit, s1, s2),
        dashboard.lowStock(),
        inventory.stock(),
      ]);
      setStats(st.data.data);
      setTopProds(tp.data.data || {});
      setTopCusts(tc.data.data || {});
      setLowStock(ls.data.data || []);
      setInvData(inv.data.data || { saleProducts: [], rentalProducts: [] });
    } catch {
      setError('Failed to load dashboard data. Check API connection.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, limit]);

  useEffect(() => { load(); }, [load]);

  const sortArr  = (arr, key) => !arr?.length ? [] : [...arr].sort((a, b) =>
    prodSort === 'desc' ? Number(b[key]||0) - Number(a[key]||0) : Number(a[key]||0) - Number(b[key]||0));

  const sortCust = (arr, key) => !arr?.length ? [] : [...arr].sort((a, b) =>
    custSort === 'desc' ? Number(b[key]||0) - Number(a[key]||0) : Number(a[key]||0) - Number(b[key]||0));

  const [start, end] = dateRange || [];
  const rangeLabel = start && end
    ? `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`
    : 'All time';

  const totalProducts = Number(stats?.totalProducts || 0);
  const activeRentals = Number(stats?.activeRentals || 0);
  const saleProds     = invData.saleProducts  || [];
  const rentProds     = invData.rentalProducts || [];
  // Count of distinct products that have any qty with customers (not raw qty sum)
  const productsOut   = rentProds.filter(p => Number(p.withCustomers || 0) > 0).length;
  // Count of distinct products currently at service center
  const inServiceCount = rentProds.filter(p => Number(p.inService || 0) > 0).length;

  // ── table columns ─────────────────────────────────────────────────────────
  const rankCol = {
    title: '#', key: 'rank', width: 36,
    render: (_, __, i) => <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{i + 1}</span>,
  };

  const prodCols = [
    rankCol,
    { title: 'Product', dataIndex: 'productname', key: 'productname',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{v}</span> },
    { title: 'Bill Amount', dataIndex: 'totalamount', key: 'totalamount', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: '#58a6ff' }}>{fmtCurrency(v)}</span> },
    { title: 'Qty', dataIndex: 'totalqty', key: 'totalqty', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmtQty(v)}</span> },
  ];

  const custBillCols = [
    rankCol,
    { title: 'Customer', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{v}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{r.phone}</div>
        </div>
      )},
    { title: 'Bill Amount', dataIndex: 'totalBill', key: 'totalBill', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: '#58a6ff' }}>{fmtCurrency(v)}</span> },
  ];

  const custPendingCols = [
    rankCol,
    { title: 'Customer', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{v}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{r.phone}</div>
        </div>
      )},
    { title: 'Pending', dataIndex: 'outstanding', key: 'outstanding', align: 'right',
      render: v => (
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: Number(v) > 0 ? '#f85149' : 'var(--text-muted)' }}>
          {fmtCurrency(v)}
        </span>
      )},
  ];

  const lowStockCols = [
    { title: 'Product', dataIndex: 'productname', key: 'productname',
      render: v => <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{v}</span> },
    { title: 'Unit', dataIndex: 'unit', key: 'unit',
      render: v => <Tag style={{ fontSize: 10 }}>{v}</Tag> },
    { title: 'Stock', dataIndex: 'stock', key: 'stock', align: 'right',
      render: v => <Tag color={Number(v) <= 0 ? 'error' : 'warning'} style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmtQty(v)}</Tag> },
    { title: 'Alert ≤', dataIndex: 'lowstockthreshold', key: 'lowstockthreshold', align: 'right',
      render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v}</span> },
  ];

  const saleInvCols = [
    { title: 'Product', dataIndex: 'productName', key: 'productName',
      render: v => <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{v}</span> },
    { title: 'Unit', dataIndex: 'unit', key: 'unit',
      render: v => <Tag style={{ fontSize: 10 }}>{v}</Tag> },
    { title: 'In Stock', dataIndex: 'stock', key: 'stock', align: 'right',
      render: (v, r) => {
        const n = Number(v || 0), t = Number(r.lowStockThreshold || 0);
        return <Tag color={n <= 0 ? 'error' : t > 0 && n <= t ? 'warning' : 'success'} style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmtQty(n)}</Tag>;
      }},
  ];

  const rentInvCols = [
    { title: 'Product', dataIndex: 'productName', key: 'productName',
      render: v => <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{v}</span> },
    { title: 'Unit', dataIndex: 'unit', key: 'unit',
      render: v => <Tag style={{ fontSize: 10 }}>{v}</Tag> },
    { title: 'In Storage', dataIndex: 'inStorage', key: 'inStorage', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#3fb950' }}>{fmtQty(v)}</span> },
    { title: 'With Customers', dataIndex: 'withCustomers', key: 'withCustomers', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#b57bee' }}>{fmtQty(v)}</span> },
    { title: 'Total', key: 'total', align: 'right',
      render: (_, r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtQty(Number(r.totalQuantity || 0))}</span> },
  ];

  const handleExcel = () => {
    const label = start && end ? `${start.format('YYYYMMDD')}_${end.format('YYYYMMDD')}` : 'all';
    exportToExcel([
      { name: 'Summary', headers: ['Metric','Value'], rows: [
        { Metric: 'Total Bill Amount',   Value: Number(stats?.totalBillAmount || 0) },
        { Metric: 'Total Payments',      Value: Number(stats?.totalCollected  || 0) },
        { Metric: 'Outstanding Credits', Value: Number(stats?.pendingAmount   || 0) },
        { Metric: 'Active Rentals',      Value: activeRentals },
        { Metric: 'Total Products',      Value: totalProducts },
      ]},
      { name: 'Top Products by Bill Amt', headers: ['Rank','Product','Amount','Qty'],
        rows: sortArr(topProds.byAmount, 'totalamount').map((d,i) => ({ Rank:i+1, Product:d.productname, Amount:Number(d.totalamount||0), Qty:Number(d.totalqty||0) })) },
      { name: 'Top Customers by Bill', headers: ['Rank','Customer','Phone','Bill Amount'],
        rows: sortCust(topCusts.byBill, 'totalBill').map((d,i) => ({ Rank:i+1, Customer:d.name, Phone:d.phone, 'Bill Amount':Number(d.totalBill||0) })) },
      { name: 'Top Customers by Pending', headers: ['Rank','Customer','Phone','Pending'],
        rows: sortCust(topCusts.byPending, 'outstanding').map((d,i) => ({ Rank:i+1, Customer:d.name, Phone:d.phone, Pending:Number(d.outstanding||0) })) },
      { name: 'Low Stock Alerts', headers: ['Product','Stock','Unit','Alert'],
        rows: lowStock.map(s => ({ Product:s.productname, Stock:Number(s.stock||0), Unit:s.unit, Alert:s.lowstockthreshold })) },
      { name: 'Sale Inventory', headers: ['Product','Unit','Stock'],
        rows: saleProds.map(p => ({ Product:p.productName, Unit:p.unit, Stock:Number(p.stock||0) })) },
      { name: 'Rental Inventory', headers: ['Product','Unit','In Storage','With Customers','Total'],
        rows: rentProds.map(p => ({ Product:p.productName, Unit:p.unit, 'In Storage':Number(p.inStorage||0), 'With Customers':Number(p.withCustomers||0), Total:Number(p.totalQuantity||0) })) },
    ], `dashboard_${label}.xlsx`);
  };

  return (
    <div style={{ paddingBottom: 40 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Row justify="space-between" align="top" gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Dashboard</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />{rangeLabel}
          </div>
        </Col>
        <Col>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              allowClear size="small"
              style={{ width: 230 }}
              presets={[
                { label: 'Today',        value: [dayjs(), dayjs()] },
                { label: 'This Week',    value: [dayjs().startOf('week'), dayjs()] },
                { label: 'This Month',   value: [dayjs().startOf('month'), dayjs()] },
                { label: 'Last Month',   value: [dayjs().subtract(1,'month').startOf('month'), dayjs().subtract(1,'month').endOf('month')] },
                { label: 'This Quarter', value: [dayjs().startOf('quarter'), dayjs()] },
                { label: 'This Year',    value: [dayjs().startOf('year'), dayjs()] },
              ]}
            />
            <Select size="small" value={limit} onChange={setLimit} style={{ width: 80 }}>
              {LIMIT_OPTS.map(n => <Option key={n} value={n}>{n === 0 ? 'All' : `Top ${n}`}</Option>)}
            </Select>
            <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExcel} type="primary">Excel</Button>
          </Space>
        </Col>
      </Row>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {loading ? (
        <Row justify="center" style={{ padding: 80 }}>
          <Col><Spin size="large" /></Col>
        </Row>
      ) : (
        <>
          {/* ── KPI Cards ──────────────────────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Total Bill Amount" value={fmtCurrency(stats?.totalBillAmount)}
                icon={<FileDoneOutlined />} color="#58a6ff" sub="Sale + Rental in period" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Total Payments" value={fmtCurrency(stats?.totalCollected)}
                icon={<WalletOutlined />} color="#3fb950" sub="Collected in period" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Outstanding Credits" value={fmtCurrency(stats?.pendingAmount)}
                icon={<WarningOutlined />} color="#f85149" sub="Uncollected amount" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Active Rentals" value={activeRentals}
                icon={<FileDoneOutlined />} color="#b57bee" sub="Pending / partial" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Products Out / Total" value={`${productsOut} / ${totalProducts}`}
                icon={<ShopOutlined />} color="#40c4aa" sub="Products with customers / total" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="In Service / Total" value={`${inServiceCount} / ${totalProducts}`}
                icon={<BoxPlotOutlined />} color="#d29922" sub="Products at service center" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Total Customers" value={stats?.totalCustomers ?? 0}
                icon={<TeamOutlined />} color="#ffb300" sub="Registered" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Unsettled Bills" value={stats?.unsettledBillsCount ?? 0}
                icon={<WarningOutlined />} color="#f85149" sub="Rental returns not yet closed" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Total Advance Held" value={fmtCurrency(stats?.totalAdvanceHeld)}
                icon={<WalletOutlined />} color="#d29922" sub="Across all customers, net of consumed" />
            </Col>
            <Col xs={12} sm={12} md={8}>
              <KpiCard label="Total Unsettled Amount" value={fmtCurrency(stats?.totalUnsettledAmount)}
                icon={<FileDoneOutlined />} color="#f85149" sub="Raw bill balance, before advance offset" />
            </Col>
          </Row>

          {/* ── Sort Controls ──────────────────────────────────────────────── */}
          <Row gutter={[24, 8]} align="middle" style={{ marginBottom: 14 }}>
            <Col>
              <Row align="middle" gutter={8}>
                <Col>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Products
                  </span>
                </Col>
                <Col>
                  <Radio.Group value={prodSort} onChange={e => setProdSort(e.target.value)} size="small" buttonStyle="solid">
                    <Radio.Button value="desc"><RiseOutlined /> High → Low</Radio.Button>
                    <Radio.Button value="asc"><FallOutlined /> Low → High</Radio.Button>
                  </Radio.Group>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row align="middle" gutter={8}>
                <Col>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Customers
                  </span>
                </Col>
                <Col>
                  <Radio.Group value={custSort} onChange={e => setCustSort(e.target.value)} size="small" buttonStyle="solid">
                    <Radio.Button value="desc"><RiseOutlined /> High → Low</Radio.Button>
                    <Radio.Button value="asc"><FallOutlined /> Low → High</Radio.Button>
                  </Radio.Group>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* ── Top Products by Bill Amount ─────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={12}>
              <DashCard
                title={<><TrophyOutlined style={{ color: '#58a6ff', marginRight: 6 }} />Top Products — Sales</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([{ name: 'Products by Sale', headers: ['Rank','Product','Amount','Qty'],
                      rows: sortArr(topProds.byAmount, 'totalamount').map((d,i) => ({ Rank:i+1, Product:d.productname, Amount:Number(d.totalamount||0), Qty:Number(d.totalqty||0) })) }],
                      'top_products_by_sale_amount.xlsx')
                  }>Excel</Button>
                }
              >
                <RankTable data={sortArr(topProds.byAmount, 'totalamount')} columns={prodCols} rowKey="productname" />
              </DashCard>
            </Col>
            <Col xs={24} md={12}>
              <DashCard
                title={<><TrophyOutlined style={{ color: '#b57bee', marginRight: 6 }} />Top Products — Rentals</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([{ name: 'Products by Rental', headers: ['Rank','Product','Amount','Qty'],
                      rows: sortArr(topProds.rentalByAmount, 'totalamount').map((d,i) => ({ Rank:i+1, Product:d.productname, Amount:Number(d.totalamount||0), Qty:Number(d.totalqty||0) })) }],
                      'top_products_by_rental_amount.xlsx')
                  }>Excel</Button>
                }
              >
                <RankTable data={sortArr(topProds.rentalByAmount, 'totalamount')} columns={prodCols} rowKey="productname" />
              </DashCard>
            </Col>
          </Row>

          {/* ── Top Customers ──────────────────────────────────────────────── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={12}>
              <DashCard
                title={<><TeamOutlined style={{ color: '#58a6ff', marginRight: 6 }} />Top Customers — by Bill Amount</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([{ name: 'Cust by Bill', headers: ['Rank','Customer','Phone','Bill Amount'],
                      rows: sortCust(topCusts.byBill, 'totalBill').map((d,i) => ({ Rank:i+1, Customer:d.name, Phone:d.phone, 'Bill Amount':Number(d.totalBill||0) })) }],
                      'customers_by_bill.xlsx')
                  }>Excel</Button>
                }
              >
                <RankTable data={sortCust(topCusts.byBill, 'totalBill')} columns={custBillCols} rowKey="id" />
              </DashCard>
            </Col>
            <Col xs={24} md={12}>
              <DashCard
                title={<><WarningOutlined style={{ color: '#f85149', marginRight: 6 }} />Top Customers — by Pending Amount</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([{ name: 'Cust by Pending', headers: ['Rank','Customer','Phone','Pending'],
                      rows: sortCust(topCusts.byPending, 'outstanding').map((d,i) => ({ Rank:i+1, Customer:d.name, Phone:d.phone, Pending:Number(d.outstanding||0) })) }],
                      'customers_by_pending.xlsx')
                  }>Excel</Button>
                }
              >
                <RankTable data={sortCust(topCusts.byPending, 'outstanding')} columns={custPendingCols} rowKey="id" />
              </DashCard>
            </Col>
          </Row>

          {/* ── Low Stock + Inventory ───────────────────────────────────────── */}
          <Row gutter={[12, 12]}>
            <Col xs={24} md={10}>
              <DashCard
                title={<><WarningOutlined style={{ color: '#d29922', marginRight: 6 }} />Low Stock Alerts</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([{ name: 'Low Stock', headers: ['Product','Stock','Unit','Alert'],
                      rows: lowStock.map(s => ({ Product:s.productname, Stock:Number(s.stock||0), Unit:s.unit, Alert:s.lowstockthreshold })) }],
                      'low_stock.xlsx')
                  }>Excel</Button>
                }
              >
                {lowStock.length === 0 ? (
                  <Row justify="center" style={{ padding: '32px 0' }}>
                    <Col style={{ color: '#3fb950', fontSize: 13 }}>✓ All items well stocked</Col>
                  </Row>
                ) : (
                  <RankTable data={lowStock} columns={lowStockCols} rowKey="productname" />
                )}
              </DashCard>
            </Col>

            <Col xs={24} md={14}>
              <DashCard
                title={<><BoxPlotOutlined style={{ color: '#40c4aa', marginRight: 6 }} />Inventory</>}
                extra={
                  <Button size="small" icon={<DownloadOutlined />} onClick={() =>
                    exportToExcel([
                      { name: 'Sale Inventory', headers: ['Product','Unit','Stock'],
                        rows: saleProds.map(p => ({ Product:p.productName, Unit:p.unit, Stock:Number(p.stock||0) })) },
                      { name: 'Rental Inventory', headers: ['Product','Unit','In Storage','With Customers','Total'],
                        rows: rentProds.map(p => ({ Product:p.productName, Unit:p.unit, 'In Storage':Number(p.inStorage||0), 'With Customers':Number(p.withCustomers||0), Total:Number(p.totalQuantity||0) })) },
                    ], 'inventory.xlsx')
                  }>Excel</Button>
                }
              >
                <Tabs
                  size="small"
                  items={[
                    { key: 'sale',   label: `Sale (${saleProds.length})`,
                      children: <RankTable data={saleProds} columns={saleInvCols} rowKey="productId" /> },
                    { key: 'rental', label: `Rental (${rentProds.length})`,
                      children: <RankTable data={rentProds} columns={rentInvCols} rowKey="productId" /> },
                  ]}
                />
              </DashCard>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}